/**
 * Modality Service
 */

import { api } from '../../lib/api'
import { API_ROUTES } from '../../config/constants'

// Re-alias para compatibilidad interna
const ROUTES = API_ROUTES.STUDY_TYPES
import type {
  Modality,
  CreateModalityData,
  UpdateModalityData,
  ModalitySearchFilters,
} from '../../domain/modality/types'

export const modalityService = {
  /**
   * List all modalities
   */
  list: async (filters?: ModalitySearchFilters): Promise<{ modalities: Modality[]; total: number }> => {
    const params = new URLSearchParams()
    if (filters?.institutionId) params.set('institutionId', filters.institutionId)
    if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive))
    if (filters?.code) params.set('code', filters.code)
    if (filters?.limit) params.set('limit', String(filters.limit))
    if (filters?.offset) params.set('offset', String(filters.offset))

    const url = params.toString()
      ? `${ROUTES.BASE}?${params.toString()}`
      : ROUTES.BASE

    const response = await api.get(url)
    return response.data
  },

  /**
   * List active modalities (for dropdowns)
   */
  listActive: async (): Promise<{ modalities: Modality[] }> => {
    const response = await api.get(ROUTES.ACTIVE)
    return response.data
  },

  /**
   * Get modality by ID
   */
  getById: async (id: string): Promise<{ modality: Modality }> => {
    const response = await api.get(ROUTES.BY_ID(id))
    return response.data
  },

  /**
   * Create new modality
   */
  create: async (data: CreateModalityData): Promise<{ message: string; modality: Modality }> => {
    const response = await api.post(ROUTES.BASE, data)
    return response.data
  },

  /**
   * Update modality
   * Using POST instead of PUT for WAF compatibility
   */
  update: async (id: string, data: UpdateModalityData): Promise<{ message: string; modality: Modality }> => {
    const response = await api.post(ROUTES.UPDATE(id), data)
    return response.data
  },

  /**
   * Delete modality
   * Using POST instead of DELETE for WAF compatibility
   */
  delete: async (id: string): Promise<void> => {
    await api.post(ROUTES.DELETE(id))
  },

  /**
   * Update display order
   * Using POST for WAF compatibility
   */
  updateOrder: async (orders: Array<{ id: string; displayOrder: number }>): Promise<void> => {
    await api.post(ROUTES.ORDER, { orders })
  },

  /**
   * Toggle modality active status
   */
  toggle: async (id: string): Promise<{ message: string; modality: Modality }> => {
    const response = await api.post(ROUTES.TOGGLE(id))
    return response.data
  },
}
